import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { type RegistrationInfo } from '@lizrd/contracts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { KeyRound, FlaskConical } from 'lucide-react';
import { SsoButtons } from '../components/auth/SsoButtons';
import { Mark } from '../components/brand/Logo';
import { AuthValueRail } from '../components/auth/AuthValueRail';
import { RegionBanner, useRegionRouting } from '../components/auth/RegionChooser';
import { getRegistrationInfo, passkeysSupported } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { DEV_TOOLS } from '../lib/dev';

/** Human messages for the ?error codes the OAuth callback redirects back with. */
const SSO_ERRORS: Record<string, string> = {
  sso_failed: 'That sign-in didn’t complete. Please try again, or use your email below.',
  sso_unavailable: 'That sign-in method isn’t available yet.',
};

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const completeMfaLogin = useAuthStore((s) => s.completeMfaLogin);
  const completePasskeyLogin = useAuthStore((s) => s.completePasskeyLogin);
  const devStartOnboarding = useAuthStore((s) => s.devStartOnboarding);
  const navigate = useNavigate();

  async function onDevStartOnboarding() {
    setError(null);
    setBusy(true);
    try {
      await devStartOnboarding();
      navigate('/onboarding', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start a test onboarding.');
      setBusy(false);
    }
  }
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // When set, the password step passed and we're awaiting a second factor.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [regInfo, setRegInfo] = useState<RegistrationInfo | null>(null);

  // Data residency: learn this stack's region + settle the visitor on the right host.
  useEffect(() => {
    let active = true;
    getRegistrationInfo()
      .then((info) => {
        if (active) setRegInfo(info);
      })
      .catch(() => {
        /* non-fatal — the region banner just won't show */
      });
    return () => {
      active = false;
    };
  }, []);
  const { changeRegion } = useRegionRouting(regInfo);

  const ssoError = SSO_ERRORS[params.get('error') ?? ''];

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login(email, password);
      if ('mfaRequired' in result) {
        // Correct password, but the account has MFA on — show the code step.
        setChallengeToken(result.challengeToken);
        setBusy(false);
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Email or password is incorrect');
      setBusy(false);
    }
  }

  async function onVerifyMfa(e: FormEvent) {
    e.preventDefault();
    if (!challengeToken) return;
    setError(null);
    setBusy(true);
    try {
      await completeMfaLogin(challengeToken, code.trim());
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code is incorrect');
      setBusy(false);
    }
  }

  async function onUsePasskey() {
    if (!challengeToken) return;
    setError(null);
    setPasskeyBusy(true);
    try {
      await completePasskeyLogin(challengeToken);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in was cancelled');
      setPasskeyBusy(false);
    }
  }

  function cancelMfa() {
    setChallengeToken(null);
    setCode('');
    setError(null);
    setPassword('');
  }

  return (
    <div className="grid overflow-hidden rounded-lg border border-border bg-surface shadow-card lg:grid-cols-2">
      {/* Left rail — a light "welcome back", not a pitch. */}
      <aside className="hidden flex-col justify-between gap-8 bg-surface-2 p-8 lg:flex">
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-iris">Welcome back</p>
          <h2 className="font-display text-2xl font-bold leading-tight text-bone">
            Pick up where you left off.
          </h2>
          <p className="text-sm leading-relaxed text-muted">
            Your domains, recommendations, and realized savings are right where you left them.
          </p>
        </div>
        <AuthValueRail />
      </aside>

      {/* Right — the form. SSO-first; email/password below. Swaps to a code
          step once a password succeeds on an MFA-protected account. */}
      <div className="p-8">
        {challengeToken ? (
          <>
            <h1 className="font-display text-xl font-bold text-bone">Two-factor authentication</h1>
            <p className="mt-1 text-sm text-muted">
              Enter the 6-digit code from your authenticator app to finish signing in.
            </p>

            {error && <p className="mt-4 text-sm text-danger">{error}</p>}

            <form onSubmit={onVerifyMfa} className="mt-6 space-y-3">
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="text-center font-mono text-lg tracking-[0.4em]"
              />
              <Button type="submit" disabled={busy || !code.trim()} className="w-full">
                {busy ? 'Verifying…' : 'Verify & sign in'}
              </Button>
            </form>

            {passkeysSupported() && (
              <>
                <div className="my-4 flex items-center gap-3 text-xs text-muted">
                  <span className="h-px flex-1 bg-border" />
                  or
                  <span className="h-px flex-1 bg-border" />
                </div>
                <Button
                  variant="outline"
                  onClick={onUsePasskey}
                  disabled={passkeyBusy}
                  className="w-full"
                >
                  {passkeyBusy ? (
                    'Waiting for passkey…'
                  ) : (
                    <>
                      <KeyRound className="mr-1.5 inline-block h-4 w-4 align-text-bottom" />
                      Use a passkey
                    </>
                  )}
                </Button>
              </>
            )}

            <p className="mt-4 text-center text-xs text-muted">
              Lost your device? Enter one of your recovery codes above instead.
            </p>
            <button
              type="button"
              onClick={cancelMfa}
              className="mt-4 w-full text-center text-sm text-iris hover:text-iris-strong"
            >
              ← Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-xl font-bold text-bone">Sign in</h1>
            <p className="mt-1 text-sm text-muted">Good to see you again.</p>

            {regInfo && (
              <div className="mt-5">
                <RegionBanner info={regInfo} context="login" onChange={changeRegion} />
              </div>
            )}

            {(error ?? ssoError) && <p className="mt-4 text-sm text-danger">{error ?? ssoError}</p>}

            <div className="mt-6">
              <SsoButtons />
            </div>

            <div className="my-5 flex items-center gap-3 text-xs text-muted">
              <span className="h-px flex-1 bg-border" />
              or continue with email
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3">
              <Input
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                type="password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-4 flex justify-between text-sm">
              <Link to="/register" className="text-iris hover:text-iris-strong">
                Create account
              </Link>
              <Link to="/forgot-password" className="text-iris hover:text-iris-strong">
                Forgot password?
              </Link>
            </div>

            {/* Self-serve sandbox — try the whole product on sample data, no signup. */}
            <div className="mt-6 rounded-lg border border-hairline bg-surface-2 p-4 text-center">
              <p className="text-sm font-medium text-bone">Just want to look around?</p>
              <p className="mt-0.5 text-xs text-muted">
                Explore the full product on realistic sample data — no signup, nothing to connect.
              </p>
              <Link
                to="/try"
                target="_blank"
                rel="noopener"
                className="mt-3 inline-flex h-9 w-full items-center justify-center rounded-md border border-iris/50 bg-transparent px-4 text-sm font-medium text-iris transition-colors hover:bg-iris/10"
              >
                <Mark size={16} className="mr-1.5 inline-block align-text-bottom" />
                Try the live sandbox →
              </Link>
            </div>

            {/* Local-only onboarding test harness (VITE_DEV_TOOLS) — a fresh
                ONBOARDING org in one click, no email/re-register. */}
            {DEV_TOOLS && (
              <div className="mt-4 rounded-lg border border-dashed border-amber/50 bg-amber/5 p-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber">
                  Dev tools (local only)
                </p>
                <button
                  type="button"
                  onClick={() => void onDevStartOnboarding()}
                  disabled={busy}
                  className="mt-2 inline-flex h-9 w-full items-center justify-center rounded-md border border-amber/50 px-4 text-sm font-medium text-amber transition-colors hover:bg-amber/10 disabled:opacity-50"
                >
                  <FlaskConical className="mr-1.5 inline-block h-4 w-4 align-text-bottom" />
                  Start a fresh onboarding
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
